import type { Post } from "../types/post";
import { ensureSlug } from "./slug";

export type HomeCategory = {
  label: string;
  slug: string;
  eyebrow: string;
  description: string;
};

export const homeCategories: HomeCategory[] = [
  {
    label: "أسئلة فلسفية ليست للإجابة",
    slug: "philosophical-questions-not-for-answer",
    eyebrow: "باب رئيسي",
    description:
      "مساحة للأسئلة التي تفتح التفكير بدل أن تغلقه بإجابة سريعة."
  }
];

const pageTagPrefix = "page:";

export function normalizeCategory(category: string | null | undefined) {
  const nextCategory = category?.trim();
  return nextCategory ? nextCategory : null;
}

export function getCategoryPath(category: HomeCategory) {
  return `/page/${category.slug}`;
}

export function createCategoryFromName(name: string): HomeCategory | null {
  const label = normalizeCategory(name);

  if (!label) {
    return null;
  }

  const seededCategory = homeCategories.find(
    (category) => category.label === label
  );

  if (seededCategory) {
    return seededCategory;
  }

  return {
    label,
    slug: ensureSlug(label, "page"),
    eyebrow: "صفحة",
    description: `نصوص ومقالات مرتبطة بـ ${label}.`
  };
}

export function getCategoryBySlug(
  slug: string | null | undefined,
  categories: HomeCategory[] = homeCategories
) {
  return categories.find((category) => category.slug === slug) ?? null;
}

export function getCategoryFromTags(tags: string[]) {
  const pageTag = tags.find((tag) => tag.startsWith(pageTagPrefix));
  return pageTag ? normalizeCategory(pageTag.slice(pageTagPrefix.length)) : null;
}

export function getPostCategory(post: Pick<Post, "category" | "tags">) {
  return normalizeCategory(post.category) ?? getCategoryFromTags(post.tags);
}

export function hasPostCategory(post: Pick<Post, "category" | "tags">) {
  return Boolean(getPostCategory(post));
}

export function getCategorizedPosts(posts: Post[]) {
  return posts.filter(hasPostCategory);
}

export function stripPageTags(tags: string[]) {
  return tags.filter((tag) => !tag.startsWith(pageTagPrefix));
}

export function applyCategoryToTags(
  tags: string[],
  category: string | null | undefined
) {
  const visibleTags = stripPageTags(tags);
  const normalizedCategory = normalizeCategory(category);
  return normalizedCategory
    ? [...visibleTags, `${pageTagPrefix}${normalizedCategory}`]
    : visibleTags;
}

export function postBelongsToCategory(post: Post, category: HomeCategory) {
  return getPostCategory(post) === category.label;
}

export function getCategoryPosts(category: HomeCategory, posts: Post[]) {
  return posts.filter((post) =>
    postBelongsToCategory(post, category)
  );
}

export function getBlogPagesFromPosts(posts: Post[]) {
  const pages = new Map<string, HomeCategory>();

  for (const category of homeCategories) {
    pages.set(category.label, category);
  }

  for (const post of posts) {
    const categoryName = getPostCategory(post);
    const category = categoryName ? createCategoryFromName(categoryName) : null;

    if (category) {
      pages.set(category.label, category);
    }
  }

  return Array.from(pages.values());
}

export function getAssignedBlogPagesFromPosts(posts: Post[]) {
  return getBlogPagesFromPosts(posts).filter(
    (category) => getCategoryPosts(category, posts).length > 0
  );
}
